'use client';

import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

interface RegulationAccessTrackerProps {
  regulationId: string;
  regulationTitle: string;
  metadata?: {
    searchQuery?: string;
    matchIndex?: number;
    fromPath?: string;
  };
}

export function RegulationAccessTracker({
  regulationId,
  regulationTitle,
  metadata = {},
}: RegulationAccessTrackerProps) {
  const posthog = usePostHog();
  const trackSentRef = useRef(false);
  const startTimeRef = useRef(Date.now());

  const trackMutation = useMutation({
    mutationFn: async (payload: {
      regulationId: string;
      posthogDistinctId: string;
      sessionId?: string;
      referrer: string;
      searchQuery?: string;
      matchIndex?: number;
      fromPath?: string;
    }) => {
      const response = await fetch(`/api/regulation/${payload.regulationId}/track-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        posthog?.capture('regulation_access_tracking_failed', {
          regulation_id: payload.regulationId,
          status: response.status,
        });
        return null;
      }

      return response.json();
    },
  });

  useEffect(() => {
    if (trackSentRef.current) return;
    trackSentRef.current = true;

    posthog?.capture('regulation_access', {
      regulation_id: regulationId,
      regulation_title: regulationTitle,
    });

    const distinctId = posthog?.get_distinct_id() || 'anonymous';
    const sessionId = posthog?.get_property('$session_id') as string | undefined;

    trackMutation.mutate({
      regulationId,
      posthogDistinctId: distinctId,
      sessionId,
      referrer: document.referrer,
      ...metadata,
    });

    return () => {
      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
    };
  }, [regulationId, posthog, JSON.stringify(metadata)]);

  return null;
}
